import { Container, Row, Col, Form, Card } from 'react-bootstrap';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sparkle from '../../../../components/Sparkle/Sparkle';
import { useNavigate } from 'react-router-dom';
import { RiSparklingFill } from '@remixicon/react';
import SpinnerButton from '../../../../components/Button/SpinnerButton';
import { fakeResponse } from '../../../../utils/helpers.js';
import { authFetch } from '../../../../utils/apiClient.js';
import { mockPP } from '../../../../utils/mockData';
import AccessModal from '../../../../components/Modal/AccessModal.jsx';
import useAccessType from '../../../../hooks/useAccessType.js';
import DemoModal from '../../../../components/Modal/DemoModal.jsx';

function PPFormScreen() {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        companyName: '',
        websiteLink: '',
        language: '',
    });
    const [validated, setValidated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [modalType, setModalType] = useState(null);
    const { getAccessType, isDemo } = useAccessType(true);
    const [showModal, setShowModal] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    async function generatePP(ppData) {
        try {
            const res = await authFetch("/api/terms/generate-pp", {
                method: "POST",
                body: JSON.stringify(ppData),
            });

            return await res.json()
        } catch (error) {
            console.error("Error generating privacy policy:", error);
            return null;
        }
    }

    async function generateFakePP() {
        return fakeResponse(mockPP)
    }

    const handleDemo = async () => {
        setIsLoading(true);
        const data = await generateFakePP();
        navigate('/terms/pp/generated', { state: { pp: data } });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            setValidated(true);
            return;
        }

        setValidated(false);

        if (isDemo) {
            setShowModal(true);
            return;
        }

        const accessType = getAccessType();
        if (accessType) {
            setModalType(accessType);
            setShowModal(true);
            return;
        }

        setIsLoading(true);
        const data = await generatePP(formData);
        navigate('/terms/pp/generated', { state: { pp: data } });
    };

    return (
        <>
            {isLoading ? (
                <Sparkle />
            ) : (
                <Container className="py-5">
                    <Row className="mb-5">
                        <Col xs={12} className="text-center">
                            <h2 className="title">{t('pp.title')}</h2>
                        </Col>
                    </Row>

                    <Card>
                        <Card.Body>
                            <Form noValidate validated={validated} onSubmit={handleSubmit} id="ppForm">
                                <Row className="mb-4 g-4">
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="companyName">
                                            <Form.Label>{t('pp.companyName')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('pp.companyNameRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="websiteLink">
                                            <Form.Label>{t('pp.websiteLink')} *</Form.Label>
                                            <Form.Control
                                                type="url"
                                                name="websiteLink"
                                                value={formData.websiteLink}
                                                onChange={handleChange}
                                                placeholder="https://..."
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('pp.urlInvalid')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-4 g-4">
                                    <Col xs={12}>
                                        <Form.Group controlId="language">
                                            <Form.Label>{t('app.language')} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="language"
                                                value={formData.language}
                                                onChange={handleChange}
                                                placeholder="Français, English, Deutsch..."
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {t('pp.languageRequired')}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} md={{ span: 6, offset: 6 }}>
                                        <SpinnerButton
                                            type="submit"
                                            className="w-100 bg-pink"
                                            disabled={isLoading}
                                            label={<>
                                                <span className="me-2">{t('app.generate')}</span>
                                                <RiSparklingFill size={20} />
                                            </>}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>

                    <AccessModal
                        show={showModal && !isDemo}
                        setShow={setShowModal}
                        modalType={modalType}
                    />

                    <DemoModal
                        show={showModal && isDemo}
                        setShow={setShowModal}
                        onAction={handleDemo}
                    />
                </Container>
            )}
        </>
    );
}

export default PPFormScreen;
